import React from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ShoppingCart, Edit, Trash2, Calendar, Search, Grid, List, ChevronLeft, ChevronRight, Eye, RefreshCw } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { format } from "date-fns";
import type { Order, Client, Supplier } from "../../../shared/schema";
import OrderDetailView from "../components/order-detail-view";
import { getCurrentUser, getAuthToken, getTenantId } from "@/lib/auth";

// Form schema
const orderSchema = z.object({
  orderNumber: z.string().optional(),
  clientId: z.number().optional().nullable(),
  supplierId: z.number().optional().nullable(),
  description: z.string().min(1, "Description is required"),
  quantity: z.number().min(0, "Quantity must be non-negative"),
  units: z.string().optional(),
  expectedDeliveryDate: z.string().optional(),
  status: z.enum(["pending", "confirmed", "in_transit", "delivered", "cancelled"])
});

type OrderFormData = z.infer<typeof orderSchema>;

export default function Orders() {
  const { toast } = useToast();
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDetailViewOpen, setIsDetailViewOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [clientFilter, setClientFilter] = useState("all");
  const [supplierFilter, setSupplierFilter] = useState("all");
  const [viewMode, setViewMode] = useState<"cards" | "list">("cards");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Fetch orders
  const { data: orders = [], isLoading, refetch } = useQuery({
    queryKey: ["/api/orders"],
    queryFn: async () => {
      const token = getAuthToken();
      const tenantId = getTenantId();
      //if (tenantId != "aaeab356-025b-4dcf-a380-03c181b5f7c1") { // Bulk Connections Tenant ID
      if (tenantId == "5068b5ba-34a7-4088-a6c7-f1cb26d6667c") { // access
        return [];
      }
      const response = await fetch("/api/orders", {
        headers: {
          "Authorization": `Bearer ${token}`,
          "X-Tenant-Id": tenantId || ""
        }
      });
      if (!response.ok) return null;
      return response.json();
    }
    
  });

  // Fetch clients for dropdowns
  const { data: clients = [] } = useQuery<Client[]>({
    queryKey: ["/api/clients"],
  });

  // Fetch suppliers for dropdowns
  const { data: suppliers = [] } = useQuery<Supplier[]>({
    queryKey: ["/api/suppliers"],
  });

  const { data: currentTenant } = useQuery({
    queryKey: ["/api/tenants/current"],
    queryFn: async () => {
      const token = getAuthToken();
      const tenantId = getTenantId();


      const response = await fetch("/api/tenants/current", {
        headers: {
          "Authorization": `Bearer ${token}`,
          "X-Tenant-Id": tenantId || ""
        }
      });
      if (!response.ok) return null;
      return response.json();
    },
  });

  // Update order mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<OrderFormData> }) =>
      apiRequest(`/api/orders/${id}`, "PUT", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/orders"] });
      setIsEditDialogOpen(false);
      setSelectedOrder(null);
      toast({
        title: "Success",
        description: "Order updated successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update order",
        variant: "destructive",
      });
    },
  });

  // Delete order mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiRequest(`/api/orders/${id}`, "DELETE"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/orders"] });
      toast({
        title: "Success",
        description: "Order deleted successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete order",
        variant: "destructive",
      });
    },
  });

  // Forms
  const editForm = useForm<OrderFormData>({
    resolver: zodResolver(orderSchema),
  });

  const onEditSubmit = (data: OrderFormData) => {
    if (selectedOrder) {
      const formattedData = {
        ...data,
        expectedDeliveryDate: data.expectedDeliveryDate ? new Date(data.expectedDeliveryDate).toISOString() : undefined
      };
      updateMutation.mutate({ id: selectedOrder.id, data: formattedData });
    }
  };

  const handleEdit = (order: Order) => {
    setSelectedOrder(order);
    editForm.reset({
      orderNumber: order.orderNumber,
      clientId: order.clientId,
      supplierId: order.supplierId,
      description: order.specialInstructions || order.product,
      quantity: Number(order.quantity) || 0,
      units: order.unit || "",
      expectedDeliveryDate: order.requestedDeliveryDate ?
        format(new Date(order.requestedDeliveryDate), "yyyy-MM-dd") : "",
      status: order.status as "pending" | "confirmed" | "in_transit" | "delivered" | "cancelled",
    });
    setIsEditDialogOpen(true);
  };

  const handleDelete = (id: number) => {
    if (confirm("Are you sure you want to delete this order?")) {
      deleteMutation.mutate(id);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refetch();
      toast({
        title: "Success",
        description: "Orders refreshed successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to refresh orders",
        variant: "destructive",
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "confirmed": return "default";
      case "in_transit": return "secondary";
      case "delivered": return "default";
      case "cancelled": return "destructive";
      default: return "outline";
    }
  };

  const getClientName = (order: any) => {
    // console.log('Order data:', order);
    // console.log('customerName:', order?.customerName);
    return order?.customerName || "No Client";
  };

  const getCurrentMineName = () => {
    return currentTenant?.name || currentTenant?.tenantCode || "Current Mine";
  };

  const getSupplierName = (order: any) => {
    return order?.collectionPoint || getCurrentMineName();
  };

  // Filter orders based on all criteria
  const allFilteredOrders = (orders as Order[]).filter((order: any) => {
    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();

      const matchesSearch = (
        (order.orderNumber ?? "").toLowerCase().includes(searchLower) ||
        (order.customerName ?? "").toLowerCase().includes(searchLower) ||
        (getSupplierName(order)).toLowerCase().includes(searchLower) ||
        (order.product ?? "").toLowerCase().includes(searchLower) ||
        (order.specialInstructions ?? "").toLowerCase().includes(searchLower) ||
        order.status.toLowerCase().includes(searchLower)
      );

      if (!matchesSearch) return false;
    }

    // Status filter
    if (statusFilter !== "all" && order.status !== statusFilter) {
      return false;
    }

    // Client filter
    if (clientFilter !== "all" && order.clientId?.toString() !== clientFilter) {
      return false;
    }

    // Supplier filter
    if (supplierFilter !== "all" && order.supplierId?.toString() !== supplierFilter) {
      return false;
    }

    return true;
  });

  // Pagination logic
  const totalItems = allFilteredOrders.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const filteredOrders = allFilteredOrders.slice(startIndex, endIndex);

  // Reset to first page when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, clientFilter, supplierFilter]);

  return (
    <div>
      <div className="space-y-6 p-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold" data-testid="text-page-title">Orders</h1>
            <p className="text-muted-foreground">Manage delivery orders and shipments</p>
          </div>
        </div>

        <Card>
          <CardHeader>
            <div className="flex justify-between items-start">
              <div>
                <CardTitle>Orders</CardTitle>
                <CardDescription>
                  Showing {startIndex + 1}-{Math.min(endIndex, totalItems)} of {totalItems} orders
                  {(statusFilter !== "all" || clientFilter !== "all" || supplierFilter !== "all" || searchTerm) && " (filtered)"}
                  {" • Total in system: "}{(orders as Order[]).length}
                </CardDescription>
              </div>
            </div>

            {/* Search and Filters */}
            <div className="space-y-4">
              <div className="flex flex-col lg:flex-row gap-4">
                {/* Search Input */}
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                  <Input
                    placeholder="Search orders by number, client, supplier, product, or status..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    data-testid="input-search-orders"
                  />
                </div>

                {/* View Toggle */}
                <div className="flex gap-2">
                  <Button
                    variant={viewMode === "cards" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setViewMode("cards")}
                    data-testid="button-card-view"
                  >
                    <Grid className="w-4 h-4" />
                  </Button>
                  <Button
                    variant={viewMode === "list" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setViewMode("list")}
                    data-testid="button-list-view"
                  >
                    <List className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRefresh}
                    disabled={isRefreshing}
                    data-testid="button-refresh-orders"
                  >
                    <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
              </div>

              {/* Filters */}
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger data-testid="select-status-filter">
                      <SelectValue placeholder="Filter by status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="confirmed">Confirmed</SelectItem>
                      <SelectItem value="in_transit">In Transit</SelectItem>
                      <SelectItem value="delivered">Delivered</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex-1">
                  <Select value={clientFilter} onValueChange={setClientFilter}>
                    <SelectTrigger data-testid="select-client-filter">
                      <SelectValue placeholder="Filter by client" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Clients</SelectItem>
                      {(clients as Client[]).map((client: Client) => (
                        <SelectItem key={client.id} value={client.id.toString()}>
                          {client.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex-1">
                  <Select value={supplierFilter} onValueChange={setSupplierFilter}>
                    <SelectTrigger data-testid="select-supplier-filter">
                      <SelectValue placeholder="Filter by supplier" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Suppliers</SelectItem>
                      {(suppliers as Supplier[]).map((supplier: Supplier) => (
                        <SelectItem key={supplier.id} value={supplier.id.toString()}>
                          {supplier.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Clear Filters */}
                {(statusFilter !== "all" || clientFilter !== "all" || supplierFilter !== "all" || searchTerm) && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSearchTerm("");
                      setStatusFilter("all");
                      setClientFilter("all");
                      setSupplierFilter("all");
                    }}
                    data-testid="button-clear-filters"
                  >
                    Clear Filters
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-4">Loading orders...</div>
            ) : filteredOrders.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <ShoppingCart className="w-12 h-12 mx-auto mb-4 opacity-50" />
                {searchTerm ? (
                  <>
                    <p>No orders match your search</p>
                    <p className="text-sm">Try adjusting your search terms</p>
                  </>
                ) : (
                  <>
                    <p>No orders found</p>
                    <p className="text-sm">Click "Add Order" to get started</p>
                  </>
                )}
              </div>
            ) : viewMode === "cards" ? (
              <div className="space-y-4">
                {filteredOrders.map((order: Order) => (
                  <div
                    key={order.id}
                    className="p-4 border rounded-lg hover:bg-accent/50 transition-colors"
                    data-testid={`card-order-${order.id}`}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-medium" data-testid={`text-order-number-${order.id}`}>
                            {order.orderNumber}
                          </h3>
                        </div>

                        <p className="text-sm text-muted-foreground mb-2">
                          {order.specialInstructions || order.product || "No description"}
                        </p>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                          <div>
                            <span className="font-medium">Client:</span> {getClientName(order)}
                          </div>
                          <div>
                            <span className="font-medium">Collection Point:</span> {getSupplierName(order)}
                          </div>
                          <div>
                            <span className="font-medium">Quantity:</span> {order.quantity || 0} {order.unit || ""}
                          </div>
                        </div>

                        {order.requestedDeliveryDate && (
                          <div className="flex items-center gap-1 text-sm text-muted-foreground mt-2">
                            <Calendar className="w-4 h-4" />
                            Expected: {format(new Date(order.requestedDeliveryDate), "MMM dd, yyyy")}
                          </div>
                        )}
                      </div>

                      <div className="flex gap-2 ml-4">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={async (e) => {
                            e.stopPropagation();
                            try {
                              const response = await fetch(`/api/orders/${order.id}?expand=all`);
                              if (response.ok) {
                                const expandedOrder = await response.json();
                                // Preserve collectionPoint from the list view
                                setSelectedOrder({
                                  ...expandedOrder,
                                  collectionPoint: order.collectionPoint,
                                  customerName: order.customerName
                                });
                                setIsDetailViewOpen(true);
                              } else {
                                // Fallback to basic order data
                                const enrichedOrder = {
                                  ...order,
                                  client: clients.find(c => c.id === order.clientId),
                                  supplier: suppliers.find(s => s.id === order.supplierId)
                                };
                                setSelectedOrder(enrichedOrder);
                                setIsDetailViewOpen(true);
                              }
                            } catch (error) {
                              console.error('Failed to fetch expanded order details:', error);
                              // Fallback to basic order data
                              const enrichedOrder = {
                                ...order,
                                client: clients.find(c => c.id === order.clientId),
                                supplier: suppliers.find(s => s.id === order.supplierId)
                              };
                              setSelectedOrder(enrichedOrder);
                              setIsDetailViewOpen(true);
                            }
                          }}
                          data-testid={`button-details-${order.id}`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Details
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(order);
                          }}
                          data-testid={`button-edit-${order.id}`}
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(order.id);
                          }}
                          data-testid={`button-delete-${order.id}`}
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="w-[150px] font-semibold text-gray-900">Order Details</TableHead>
                      <TableHead className="font-semibold text-gray-900">Product & Quantity</TableHead>
                      <TableHead className="font-semibold text-gray-900">Client</TableHead>
                      <TableHead className="font-semibold text-gray-900">Collection Point</TableHead>
                      <TableHead className="font-semibold text-gray-900">Important Dates</TableHead>
                      <TableHead className="text-right font-semibold text-gray-900">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredOrders.map((order: Order) => (
                      <TableRow key={order.id} data-testid={`row-order-${order.id}`} className="hover:bg-blue-50 transition-colors">
                        <TableCell className="font-medium" data-testid={`cell-order-number-${order.id}`}>
                          <div className="space-y-1">
                            <div className="font-mono text-sm font-bold text-blue-600">{order.orderNumber}</div>
                            <div className="text-xs text-gray-500">
                              {order.createdAt ? new Date(order.createdAt).toLocaleDateString() : 'No date'}
                            </div>
                            {order.referenceNumber && (
                              <div className="text-xs text-gray-400">Ref: {order.referenceNumber}</div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell data-testid={`cell-product-${order.id}`}>
                          <div className="max-w-[200px] space-y-1">
                            <div className="font-medium text-gray-900">{order.product || "Transportation Service"}</div>
                            <div className="text-sm font-medium text-green-600">
                              {order.quantity ? `${Number(order.quantity).toLocaleString()} ${order.unit || ''}` : 'Qty: N/A'}
                            </div>
                            {order.expectedWeight && (
                              <div className="text-xs text-blue-600">Est: {Number(order.expectedWeight).toLocaleString()} kg</div>
                            )}
                            {order.specialInstructions && (
                              <div className="text-xs text-gray-500 truncate" title={order.specialInstructions}>
                                📝 {order.specialInstructions}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell data-testid={`cell-client-${order.id}`}>
                          <div className="max-w-[180px] space-y-1">
                            <div className="font-medium text-gray-900">{getClientName(order)}</div>
                            {order.originAddress && (
                              <div className="text-xs text-gray-500 truncate" title={order.originAddress}>
                                📍 {order.originAddress}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell data-testid={`cell-supplier-${order.id}`}>
                          <div className="max-w-[180px] space-y-1">
                            <div className="font-medium text-gray-900">{getSupplierName(order)}</div>
                            {order.destinationAddress && (
                              <div className="text-xs text-gray-500 truncate" title={order.destinationAddress}>
                                🎯 {order.destinationAddress}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell data-testid={`cell-delivery-${order.id}`}>
                          <div className="space-y-1 text-sm">
                            {order.requestedPickupDate && (
                              <div className="flex items-center gap-1 text-blue-600">
                                <span className="text-xs">📤</span>
                                <span className="text-xs font-medium">Pickup:</span>
                                {format(new Date(order.requestedPickupDate), "MMM dd")}
                              </div>
                            )}
                            {order.requestedDeliveryDate && (
                              <div className="flex items-center gap-1 text-green-600">
                                <span className="text-xs">📥</span>
                                <span className="text-xs font-medium">Delivery:</span>
                                {format(new Date(order.requestedDeliveryDate), "MMM dd")}
                              </div>
                            )}
                            {!order.requestedPickupDate && !order.requestedDeliveryDate && (
                              <span className="text-gray-400 text-xs">No dates set</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={async () => {
                                try {
                                  const response = await fetch(`/api/orders/${order.id}?expand=all`);
                                  if (response.ok) {
                                    const expandedOrder = await response.json();
                                    setSelectedOrder(expandedOrder);
                                    setIsDetailViewOpen(true);
                                  } else {
                                    // Fallback to basic order data
                                    const enrichedOrder = {
                                      ...order,
                                      client: clients.find(c => c.id === order.clientId),
                                      supplier: suppliers.find(s => s.id === order.supplierId)
                                    };
                                    setSelectedOrder(enrichedOrder);
                                    setIsDetailViewOpen(true);
                                  }
                                } catch (error) {
                                  console.error('Failed to fetch expanded order details:', error);
                                  // Fallback to basic order data
                                  const enrichedOrder = {
                                    ...order,
                                    client: clients.find(c => c.id === order.clientId),
                                    supplier: suppliers.find(s => s.id === order.supplierId)
                                  };
                                  setSelectedOrder(enrichedOrder);
                                  setIsDetailViewOpen(true);
                                }
                              }}
                              data-testid={`button-details-table-${order.id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEdit(order)}
                              data-testid={`button-edit-table-${order.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDelete(order.id)}
                              data-testid={`button-delete-table-${order.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {/* Pagination Controls */}
            {totalItems > 0 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span>Show</span>
                  <Select
                    value={itemsPerPage.toString()}
                    onValueChange={(value) => {
                      setItemsPerPage(parseInt(value));
                      setCurrentPage(1);
                    }}
                  >
                    <SelectTrigger className="w-20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="25">25</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
                  <span>per page</span>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    data-testid="button-previous-page"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Previous
                  </Button>

                  <div className="flex items-center gap-1">
                    {/* Show page numbers */}
                    {(() => {
                      const pages = [];
                      const showPages = 5; // Show max 5 page numbers
                      let startPage = Math.max(1, currentPage - Math.floor(showPages / 2));
                      let endPage = Math.min(totalPages, startPage + showPages - 1);

                      // Adjust start page if we're near the end
                      if (endPage - startPage < showPages - 1) {
                        startPage = Math.max(1, endPage - showPages + 1);
                      }

                      // First page
                      if (startPage > 1) {
                        pages.push(
                          <Button
                            key={1}
                            variant={1 === currentPage ? "default" : "outline"}
                            size="sm"
                            onClick={() => setCurrentPage(1)}
                            className="w-8 h-8 p-0"
                          >
                            1
                          </Button>
                        );
                        if (startPage > 2) {
                          pages.push(<span key="ellipsis1" className="px-2">...</span>);
                        }
                      }

                      // Page numbers
                      for (let i = startPage; i <= endPage; i++) {
                        pages.push(
                          <Button
                            key={i}
                            variant={i === currentPage ? "default" : "outline"}
                            size="sm"
                            onClick={() => setCurrentPage(i)}
                            className="w-8 h-8 p-0"
                            data-testid={`button-page-${i}`}
                          >
                            {i}
                          </Button>
                        );
                      }

                      // Last page
                      if (endPage < totalPages) {
                        if (endPage < totalPages - 1) {
                          pages.push(<span key="ellipsis2" className="px-2">...</span>);
                        }
                        pages.push(
                          <Button
                            key={totalPages}
                            variant={totalPages === currentPage ? "default" : "outline"}
                            size="sm"
                            onClick={() => setCurrentPage(totalPages)}
                            className="w-8 h-8 p-0"
                          >
                            {totalPages}
                          </Button>
                        );
                      }

                      return pages;
                    })()}
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    data-testid="button-next-page"
                  >
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>

                <div className="text-sm text-muted-foreground">
                  Page {currentPage} of {totalPages}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Order Details Dialog */}
        <Dialog open={isDetailViewOpen} onOpenChange={(open) => {
          if (!open) {
            setSelectedOrder(null);
          }
          setIsDetailViewOpen(open);
        }}>
          <DialogContent className="max-w-7xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Order Details</DialogTitle>
            </DialogHeader>
            {selectedOrder && (
              <OrderDetailView order={selectedOrder} />
            )}
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Edit Order</DialogTitle>
              <DialogDescription>
                Update order information.
              </DialogDescription>
            </DialogHeader>
            <Form {...editForm}>
              <form onSubmit={editForm.handleSubmit(onEditSubmit)} className="space-y-4">
                <FormField
                  control={editForm.control}
                  name="orderNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Order Number</FormLabel>
                      <FormControl>
                        <Input placeholder="ORD-2024-001" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={editForm.control}
                  name="clientId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Client</FormLabel>
                      <Select
                        onValueChange={(value) => field.onChange(value ? parseInt(value) : null)}
                        value={field.value?.toString() || ""}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select client" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="">No Client</SelectItem>
                          {(clients as Client[]).map((client: Client) => (
                            <SelectItem key={client.id} value={client.id.toString()}>
                              {client.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={editForm.control}
                  name="supplierId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Collection Point</FormLabel>
                      <Select
                        onValueChange={(value) => field.onChange(value ? parseInt(value) : null)}
                        value={field.value?.toString() || ""}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select supplier" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="">No Supplier</SelectItem>
                          {(suppliers as Supplier[]).map((supplier: Supplier) => (
                            <SelectItem key={supplier.id} value={supplier.id.toString()}>
                              {supplier.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={editForm.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description</FormLabel>
                      <FormControl>
                        <Textarea placeholder="Order description" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={editForm.control}
                    name="quantity"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Quantity</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            placeholder="100"
                            {...field}
                            onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={editForm.control}
                    name="units"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Units</FormLabel>
                        <FormControl>
                          <Input placeholder="tons, kg, pieces" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <FormField
                  control={editForm.control}
                  name="expectedDeliveryDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Expected Delivery Date</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={editForm.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Status</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="confirmed">Confirmed</SelectItem>
                          <SelectItem value="in_transit">In Transit</SelectItem>
                          <SelectItem value="delivered">Delivered</SelectItem>
                          <SelectItem value="cancelled">Cancelled</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <DialogFooter>
                  <Button
                    type="submit"
                    disabled={updateMutation.isPending}
                  >
                    {updateMutation.isPending ? "Updating..." : "Update Order"}
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}